import { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import {
  Link,
  useFetcher,
  useLoaderData,
  useRouteLoaderData,
} from "@remix-run/react";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { getLocale } from "~/lib/i18n.server";
import { fetchPressPostsPage, type PressPost } from "~/lib/press.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData } from "~/lib/utils";

export const sitemap = createSitemapFunction("press");

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  // NB: niente hook qui (meta non è un componente). Questi sono solo i
  // fallback SEO: se la pagina WP ha lo Yoast SEO, ha la precedenza.
  return getMetaData(data, {
    title: "Press Room",
    description:
      "Notizie e contributi editoriali che parlano di noi: la rassegna stampa di ChorusLife Immersive SPA.",
  });
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  const lang = await getLocale(request);
  const wpApiHost = context.cloudflare.env.WP_API_HOST as string;
  const base = (wpApiHost || "").replace(/\/+$/, "");

  let page: any = {};
  let seo: any = undefined;
  try {
    const res = await fetch(
      `${base}/wp-json/wp/v2/pages?slug=press&lang=${encodeURIComponent(lang)}`
    );
    if (res.ok) {
      const pages: any = await res.json();
      if (Array.isArray(pages) && pages.length > 0) {
        page = pages[0];
        seo = pages[0]?.yoast_head_json;
      }
    }
  } catch (err) {
    console.error("[press] page fetch error:", err);
  }

  const { posts, totalPages } = await fetchPressPostsPage(wpApiHost, lang, 1);

  return { page, posts, seo, lang, totalPages };
}

function PressCard({ post, lang }: { post: PressPost; lang: string }) {
  const { t } = useTranslation();
  const isExternal = post.externalLink !== "";
  const readMoreClasses =
    "mt-3 inline-block text-secondary font-medium hover:underline";

  return (
    <article className="flex flex-col border-b-secondary border-b-[1px] pb-4">
      {post.featuredMediaId > 0 && (
        <img
          alt=""
          src={`/cms-asset/${post.featuredMediaId}`}
          loading="lazy"
          className="w-full h-56 object-cover object-center bg-muted"
        />
      )}
      <h3
        className="mt-4 text-xl text-secondary font-bold uppercase"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />
      <div
        className="mt-2 text-sm text-foreground/70 [&_p]:m-0 line-clamp-4"
        dangerouslySetInnerHTML={{ __html: post.excerpt }}
      />
      {isExternal ? (
        <a
          href={post.externalLink}
          target="_blank"
          rel="noopener noreferrer"
          className={readMoreClasses}
        >
          {t("Leggi")} »
        </a>
      ) : (
        <Link
          to={getMenuHref(lang, `/press/${post.slug}`)}
          className={readMoreClasses}
        >
          {t("Leggi")} »
        </Link>
      )}
    </article>
  );
}

export default function Press() {
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const { page, posts, lang, totalPages } = loaderData;
  const bookSection = page?.acf?.book_section;

  // --- Ricerca full-text su WordPress ---
  const searchFetcher = useFetcher<{ posts: PressPost[] }>();
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;

  // Debounce: ricerca full-text su WordPress (?search=) mentre l'utente digita.
  useEffect(() => {
    if (!trimmedQuery) return;
    const handle = setTimeout(() => {
      searchFetcher.load(
        `/api/press/search?q=${encodeURIComponent(
          trimmedQuery
        )}&lang=${encodeURIComponent(lang)}`
      );
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedQuery, lang]);

  const searchResults = searchFetcher.data?.posts ?? [];
  const isSearchLoading = searchFetcher.state !== "idle";

  // --- Paginazione "carica altri" della lista ---
  const loadMoreFetcher = useFetcher<{
    posts: PressPost[];
    totalPages: number;
  }>();
  const [extraPosts, setExtraPosts] = useState<PressPost[]>([]);
  const [loadedPage, setLoadedPage] = useState(1);

  useEffect(() => {
    const newPosts = loadMoreFetcher.data?.posts;
    if (!newPosts || newPosts.length === 0) return;
    setExtraPosts((prev) => {
      const seen = new Set([...posts, ...prev].map((p) => p.id));
      const toAdd = newPosts.filter((p) => !seen.has(p.id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, [loadMoreFetcher.data, posts]);

  const listPosts = [...posts, ...extraPosts];
  const hasMore = loadedPage < totalPages;
  const isLoadingMore = loadMoreFetcher.state !== "idle";

  const loadMore = () => {
    const next = loadedPage + 1;
    setLoadedPage(next);
    loadMoreFetcher.load(
      `/api/press/list?page=${next}&lang=${encodeURIComponent(lang)}`
    );
  };

  const displayedPosts = isSearching ? searchResults : listPosts;

  return (
    <div className="relative">
      <main className="flex flex-col lg:pb-12">
        {page?.acf?.hero && (
          <section
            className="bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/cms-asset/${page.acf.hero.image?.id}')`,
            }}
          >
            <div className="bg-black bg-opacity-40 py-32">
              <div className="flex flex-col max-w-4xl mx-auto items-center justify-center px-4 text-center">
                <h1 className="text-4xl lg:text-5xl text-white font-tenor-sans uppercase">
                  {page.acf?.hero?.title}
                </h1>
                <hr className="block w-16 h-[2px] bg-white mt-2" />
                <div className="text-white text-lg mt-4" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: page.acf?.hero?.description }}>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="w-full max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="max-w-md mx-auto mb-10 lg:mb-14">
            <div className="relative">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-secondary/50"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("Cerca un articolo...")}
                aria-label={t("Cerca un articolo...")}
                className="w-full border border-secondary bg-white py-3 pl-11 pr-4 text-secondary placeholder:text-secondary/40 focus:outline-none focus:ring-1 focus:ring-secondary"
              />
            </div>
          </div>

          {isSearching && isSearchLoading && searchResults.length === 0 ? (
            <p className="text-center text-foreground/60 py-12">
              {t("Ricerca in corso...")}
            </p>
          ) : displayedPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {displayedPosts.map((post) => (
                  <PressCard key={post.id} post={post} lang={lang} />
                ))}
              </div>
              {!isSearching && (hasMore || isLoadingMore) && (
                <div className="flex justify-center mt-10 lg:mt-14">
                  <Button
                    variant="secondary"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore
                      ? t("Caricamento...")
                      : t("Carica altri articoli")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-foreground/60 py-12">
              {isSearching
                ? t("Nessun articolo trovato per la tua ricerca.")
                : t("Al momento non ci sono articoli disponibili.")}
            </p>
          )}
        </section>

        {bookSection && (
          <section className="max-w-7xl mx-auto w-full px-4">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${bookSection.image?.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-20 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center text-center">
                  <h2 className="text-2xl lg:text-4xl font-tenor-sans text-white uppercase">
                    {bookSection.title}
                  </h2>
                  <div
                    className="text-white mt-4 text-lg"
                    dangerouslySetInnerHTML={{ __html: bookSection.description }}
                  />
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <Link
                      to={
                        rootData?.bookingDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/booking")
                      }
                    >
                      <Button variant="muted">{t("Prenota ora")}</Button>
                    </Link>
                    <Link
                      to={
                        rootData?.shopDisabled
                          ? getMenuHref(i18n.language, "/coming-soon")
                          : getMenuHref(i18n.language, "/shop")
                      }
                    >
                      <Button variant="secondary">{t("Regala")}</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
